import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  useGetStudentAssignmentsStatistics,
  useGetStudentsWithIds,
  useGetStudentWeeklyAnalysis,
} from "@packages/api/madlenAPI";
import { SingleStudentAssignmentsTable } from "../../components/SingleStudentAssignmentsTable";
import { Text, Button } from "@core/index";
// import FeedbackCard from "@features/students/components/FeedbackCard";

const SingleStudentView = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: students, isLoading: isLoadingStudent } = useGetStudentsWithIds(
    {
      studentIds: [Number(id)],
    },
  );

  const student = useMemo(() => students?.[0], [students]);

  const { data: assignmentStatistics, isLoading: isLoadingStats } =
    useGetStudentAssignmentsStatistics(Number(id));

  const courseDistribution = useMemo(() => {
    if (!student?.classes) return [];
    const courses: { [key: string]: number } = {};
    student.classes.forEach((cls) => {
      courses[cls.courseType] = (courses[cls.courseType] || 0) + 1;
    });
    return Object.entries(courses).map(([course, count]) => ({
      course: t(`courseType.${course}`),
      count,
    }));
  }, [student]);

  const scoreProgressData = useMemo(() => {
    if (!assignmentStatistics?.assignments) return [];
    return assignmentStatistics.assignments.map((assignment) => ({
      name: assignment.name,
      score: assignment.score,
      avgScore: assignment.avgScore,
    }));
  }, [assignmentStatistics]);

  const questionTypeSuccessData = useMemo(() => {
    if (!assignmentStatistics?.questionTypeSuccessCounts) return [];
    return assignmentStatistics.questionTypeSuccessCounts.map((type) => ({
      type: t(`assignments.generator.questions.types.${type.questionType}`),
      success: type.successCount,
    }));
  }, [assignmentStatistics]);

  const { data: studentWeeklyAnalysisData } = useGetStudentWeeklyAnalysis(
    Number(id),
  );

  console.log(studentWeeklyAnalysisData);

  if (isLoadingStudent || isLoadingStats) {
    return (
      <div className="p-4 md:p-8 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          {/* Loading Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 mb-6 animate-pulse">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-3 flex-1">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/5"></div>
              </div>
            </div>
          </div>

          {/* Loading Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 ${
                  i === 3 ? "col-span-1 md:col-span-2" : ""
                }`}
              >
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                <div className="h-[300px] bg-gray-100 dark:bg-gray-700 rounded animate-pulse flex items-center justify-center">
                  <div className="text-gray-400 dark:text-gray-500">
                    {t("common.loading")}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Loading Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-12 bg-gray-100 dark:bg-gray-700 rounded animate-pulse"
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center"
          textColor="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        {/* Student Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 mb-6 transition-all duration-300 ease-in-out hover:shadow-md">
          <div className="flex flex-col space-y-6">
            {/* Student Info Section */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
              <div className="relative">
                <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center overflow-hidden transition-all duration-300 hover:scale-105 ring-4 ring-primary-200 dark:ring-primary-800 shadow-lg">
                  <img
                    src={`/avatars/${student?.avatarName}.png`}
                    alt="Student Avatar"
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
              </div>

              <div className="text-center sm:text-left">
                <Text
                  variant={"h1"}
                  className="text-2xl sm:text-3xl lg:text-4xl tracking-tight"
                >
                  {student?.name} {student?.surname}
                </Text>
                <Text
                  variant={"p"}
                  status="subtle"
                  className="sm:text-base mt-1"
                >
                  {student?.email}
                </Text>
              </div>
            </div>

            {/* Stats Cards Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Email Card */}
              <div className="flex items-center bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="mr-4 p-2 bg-blue-500/10 rounded-lg">
                  <svg
                    className="w-8 h-8 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <Text
                    variant={"p"}
                    status="info"
                    size={"xs"}
                    className="font-medium uppercase tracking-wider"
                  >
                    students.table.email
                  </Text>
                  <Text
                    variant={"p"}
                    weight={"bold"}
                    size={"sm"}
                    className="!text-blue-700 dark:!text-blue-200 mt-1 break-all"
                  >
                    {student?.email}
                  </Text>
                </div>
              </div>

              {/* Grade Card */}
              <div className="flex items-center bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-800/30 p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="mr-4 p-2 bg-indigo-500/10 rounded-lg">
                  <svg
                    className="w-8 h-8 text-indigo-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4"
                    />
                  </svg>
                </div>
                <div>
                  <Text
                    variant={"p"}
                    size={"xs"}
                    weight={"medium"}
                    className="text-indigo-600 dark:text-indigo-300 uppercase tracking-wider"
                  >
                    {t("students.table.grade")}
                  </Text>
                  <Text
                    variant={"h1"}
                    className="!text-indigo-700 dark:!text-indigo-200 mt-1"
                  >
                    {student?.grade}
                  </Text>
                </div>
              </div>

              {/* Average Points Card */}
              {assignmentStatistics?.generalAveragePoint !== 0 &&
                assignmentStatistics?.generalAveragePoint !== undefined && (
                  <div className="flex items-center bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/30 p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="mr-4 p-2 bg-emerald-500/10 rounded-lg">
                      <svg
                        className="w-8 h-8 text-emerald-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                        />
                      </svg>
                    </div>
                    <div>
                      <Text
                        variant={"p"}
                        size={"xs"}
                        weight={"medium"}
                        className="text-emerald-600 dark:text-emerald-300 uppercase tracking-wider"
                      >
                        students.stats.generalAverage
                      </Text>
                      <Text
                        variant={"h1"}
                        className="!text-emerald-700 dark:!text-emerald-200 mt-1"
                      >
                        {assignmentStatistics.generalAveragePoint.toFixed(1)}
                      </Text>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
          {/* Question Type Success Rates */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 transition-all duration-300 ease-in-out hover:shadow-md">
            <Text variant={"h3"} className="mb-4">
              students.charts.questionTypeSuccess
            </Text>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={questionTypeSuccessData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    border: "none",
                    borderRadius: "8px",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                    color: "black",
                  }}
                />
                <Legend />
                <Bar
                  dataKey="success"
                  fill="#10b981"
                  name={t("students.charts.correctAnswers")}
                />
                {/* <Bar
                  dataKey="total"
                  fill="#6366f1"
                  name={t("students.charts.totalQuestions")}
                /> */}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Course Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 transition-all duration-300 ease-in-out hover:shadow-md">
            <Text variant={"h3"} className="mb-4">
              students.charts.courseDistribution
            </Text>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={courseDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="course" />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    border: "none",
                    borderRadius: "8px",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                    color: "black",
                  }}
                />
                <Bar
                  dataKey="count"
                  fill="#6366f1"
                  name={t("students.charts.count")}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Score Progress Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 col-span-1 md:col-span-2 transition-all duration-300 ease-in-out hover:shadow-md">
            <Text variant={"h3"} className="mb-4">
              students.charts.scoreProgress
            </Text>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={scoreProgressData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-50}
                  textAnchor="end"
                  height={100}
                  tickFormatter={(value) =>
                    value.length > 10 ? value.substring(0, 10) + "..." : value
                  }
                />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    border: "none",
                    borderRadius: "8px",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                    color: "black",
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="#6366f1"
                  name={t("students.charts.studentScore")}
                  strokeWidth={2}
                  dot={{ strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="avgScore"
                  stroke="#10b981"
                  name={t("students.charts.averageScore")}
                  strokeWidth={2}
                  dot={{ strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* {studentWeeklyAnalysisData && studentWeeklyAnalysisData.length > 0 && (
          <div className="space-y-6 mb-6">
            {studentWeeklyAnalysisData.map((feedback, index) => (
              <FeedbackCard
                key={`${feedback.studentId}-${index}`}
                feedback={feedback}
              />
            ))}
          </div>
        )} */}

        {/* Assignments Table */}
        <SingleStudentAssignmentsTable id={Number(id)} />
      </div>
    </div>
  );
};

export default SingleStudentView;
